import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { startEmailLogin, confirmEmailLogin } from '../lib/linkIdentity';

const inputCls = 'bg-[#1C1C1E] border-white/10 text-[#DCD3CA] rounded-xl h-12';

// Two steps: send a 6-digit code to the email, then confirm it and choose a password.
export const AddEmailLoginDialog = ({ open, onOpenChange, defaultEmail, emailLocked, onDone }) => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState(defaultEmail || '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = (o) => {
    if (!o) { setStep('email'); setCode(''); setPassword(''); setConfirm(''); setEmail(defaultEmail || ''); }
    onOpenChange(o);
  };

  const send = async () => {
    setBusy(true);
    try {
      await startEmailLogin(email.trim());
      toast.success('We sent a 6-digit code to your email.');
      setStep('code');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (password !== confirm) { toast.error('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await confirmEmailLogin(email.trim(), code.trim(), password);
      toast.success('Email login added. Your Apple/Google sign-in still works without a password.');
      reset(false);
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="bg-[#252528] border-white/10 text-[#DCD3CA] rounded-3xl" data-testid="add-email-login-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#DCD3CA]">Add email login</DialogTitle>
          <DialogDescription className="text-[#B5B1AB]">
            Optional. Lets you also sign in with an email and password — Apple and Google stay password-free.
          </DialogDescription>
        </DialogHeader>
        {step === 'email' ? (
          <div className="space-y-4">
            <Input
              type="email" value={email} disabled={emailLocked} placeholder="you@example.com"
              onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="email-login-email-input"
            />
            <Button onClick={send} disabled={busy || !email.trim()} className="w-full rounded-2xl py-6 bg-[#8BAA9E] text-[#1C1C1E] hover:bg-[#9DBBAF]" data-testid="email-login-send-code-btn">
              {busy ? 'Sending…' : 'Send verification code'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-[#B5B1AB]" data-testid="email-login-code-sent-to">Code sent to {email}</p>
            <Input inputMode="numeric" maxLength={6} value={code} placeholder="6-digit code" onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} className={inputCls} data-testid="email-login-code-input" />
            <Input type="password" value={password} placeholder="New password (8+ characters)" onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="email-login-password-input" />
            <Input type="password" value={confirm} placeholder="Confirm password" onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="email-login-password-confirm-input" />
            <Button onClick={save} disabled={busy || code.length !== 6 || password.length < 8} className="w-full rounded-2xl py-6 bg-[#8BAA9E] text-[#1C1C1E] hover:bg-[#9DBBAF]" data-testid="email-login-confirm-btn">
              {busy ? 'Saving…' : 'Verify and save'}
            </Button>
            <button type="button" onClick={send} disabled={busy} className="text-sm text-[#8BAA9E] w-full" data-testid="email-login-resend-btn">Resend code</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddEmailLoginDialog;
